import { useState } from 'react';
import { toast } from 'sonner';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { AdminUser } from '@/hooks/admin/useAdminUsers';
import { useUserEntities } from '@/hooks/admin/useUserEntities';
import { useUserSecurity, TokenSummary, LoginAttemptSummary, PasskeySummary } from '@/hooks/admin/useUserSecurity';
import { useClearLoginAttempts, useSendPasswordReset } from '@/hooks/admin/useAdminActions';
import { formatRelativeTime, formatTimeUntil } from '@/utils/date';

interface UserDetailsModalProps {
    user: AdminUser;
    onClose: () => void;
}

type TabType = 'entities' | 'security';

function formatTokenType(type: string): string {
    const typeMap: Record<string, string> = {
        'email_verification': 'Email Verification',
        'email_change_confirm': 'Email Change Confirm',
        'email_change_verify': 'Email Change Verify',
        'account_deletion': 'Account Deletion',
        'password_reset': 'Password Reset',
    };
    return typeMap[type] || type;
}

export function UserDetailsModal({ user, onClose }: UserDetailsModalProps) {
    const [activeTab, setActiveTab] = useState<TabType>('entities');
    const [confirmingClear, setConfirmingClear] = useState(false);
    const [confirmingSendReset, setConfirmingSendReset] = useState(false);

    const { data: entitiesData, isLoading: entitiesLoading } = useUserEntities(user.id);
    const { data: securityData, isLoading: securityLoading } = useUserSecurity(
        activeTab === 'security' ? user.id : null
    );

    const clearLoginAttempts = useClearLoginAttempts();
    const sendPasswordReset = useSendPasswordReset();

    const handleClearLoginAttempts = async () => {
        try {
            const result = await clearLoginAttempts.mutateAsync(user.id);
            toast.success(`Cleared ${result.deletedCount} failed login attempt${result.deletedCount !== 1 ? 's' : ''}`);
            setConfirmingClear(false);
        } catch {
            toast.error('Failed to clear login attempts');
        }
    };

    const handleSendPasswordReset = async () => {
        try {
            await sendPasswordReset.mutateAsync(user.id);
            toast.success('Password reset email sent successfully');
            setConfirmingSendReset(false);
        } catch {
            toast.error('Failed to send password reset email');
        }
    };

    return (
        <Dialog open={true} onClose={onClose} title="User Details" size="lg">
            {/* User info header */}
            <div className="mb-4 rounded-lg bg-gray-50 p-4 dark:bg-gray-700/50">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                    User ID: {user.id}
                </p>
                <p className="font-semibold text-gray-900 dark:text-white">
                    {user.email}
                </p>
            </div>

            {/* Tabs */}
            <div className="mb-4 border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'entities'}
                        onClick={() => setActiveTab('entities')}
                        className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium ${
                            activeTab === 'entities'
                                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                    >
                        Entities
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'security'}
                        onClick={() => setActiveTab('security')}
                        className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium ${
                            activeTab === 'security'
                                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                    >
                        Security
                    </button>
                </nav>
            </div>

            {/* Tab Content */}
            {activeTab === 'entities' && (
                <EntitiesTabContent data={entitiesData} isLoading={entitiesLoading} />
            )}

            {activeTab === 'security' && (
                <SecurityTabContent
                    data={securityData}
                    isLoading={securityLoading}
                    user={user}
                    confirmingClear={confirmingClear}
                    setConfirmingClear={setConfirmingClear}
                    confirmingSendReset={confirmingSendReset}
                    setConfirmingSendReset={setConfirmingSendReset}
                    onClearLoginAttempts={handleClearLoginAttempts}
                    onSendPasswordReset={handleSendPasswordReset}
                    isClearingLogins={clearLoginAttempts.isPending}
                    isSendingReset={sendPasswordReset.isPending}
                />
            )}

            {/* Footer */}
            <div className="mt-6 flex justify-end">
                <Button variant="secondary" onClick={onClose}>
                    Close
                </Button>
            </div>
        </Dialog>
    );
}

interface EntitiesTabContentProps {
    data: ReturnType<typeof useUserEntities>['data'];
    isLoading: boolean;
}

function EntitiesTabContent({ data, isLoading }: EntitiesTabContentProps) {
    if (isLoading) {
        return (
            <div className="flex justify-center py-8">
                <Spinner size="lg" />
            </div>
        );
    }

    if (!data) {
        return (
            <p className="text-gray-500 dark:text-gray-400">
                Failed to load data
            </p>
        );
    }

    return (
        <div className="space-y-6">
            {/* Collections */}
            <section>
                <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Collections ({data.collectionsCount})
                </h4>
                {data.collections.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        No collections
                    </p>
                ) : (
                    <ul className="max-h-40 overflow-y-auto rounded-sm border border-gray-200 dark:border-gray-600">
                        {data.collections.map((c) => (
                            <li
                                key={c.id}
                                className="flex items-center justify-between border-b border-gray-100 px-3 py-2 last:border-b-0 dark:border-gray-700"
                            >
                                <span className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                    <span className="text-xs text-gray-400">
                                        #{c.id}
                                    </span>
                                    {c.label}
                                </span>
                                <span className="text-xs text-gray-500">
                                    {c.type}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* Tags */}
            <section>
                <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Tags ({data.tagsCount})
                </h4>
                {data.tags.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        No tags
                    </p>
                ) : (
                    <ul className="max-h-40 overflow-y-auto rounded-sm border border-gray-200 dark:border-gray-600">
                        {data.tags.map((t) => (
                            <li
                                key={t.id}
                                className="flex items-center justify-between border-b border-gray-100 px-3 py-2 last:border-b-0 dark:border-gray-700"
                            >
                                <span className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                    <span className="text-xs text-gray-400">
                                        #{t.id}
                                    </span>
                                    {t.label}
                                </span>
                                <span className="text-xs text-gray-500">
                                    {t.type}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* Ledgers */}
            <section>
                <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Ledgers ({data.ledgersCount})
                </h4>
                {data.ledgers.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        No ledgers
                    </p>
                ) : (
                    <ul className="max-h-48 overflow-y-auto rounded-sm border border-gray-200 dark:border-gray-600">
                        {data.ledgers.map((l) => (
                            <li
                                key={l.id}
                                className="flex items-center justify-between border-b border-gray-100 px-3 py-2 last:border-b-0 dark:border-gray-700"
                            >
                                <span className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                    <span className="text-xs text-gray-400">
                                        #{l.id}
                                    </span>
                                    {l.label}
                                </span>
                                <span className="flex items-center gap-3">
                                    <span className="text-xs text-gray-500">
                                        {l.valuesCount} values,{' '}
                                        {l.returnsCount} returns
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {l.type}
                                    </span>
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}

interface SecurityTabContentProps {
    data: ReturnType<typeof useUserSecurity>['data'];
    isLoading: boolean;
    user: AdminUser;
    confirmingClear: boolean;
    setConfirmingClear: (value: boolean) => void;
    confirmingSendReset: boolean;
    setConfirmingSendReset: (value: boolean) => void;
    onClearLoginAttempts: () => void;
    onSendPasswordReset: () => void;
    isClearingLogins: boolean;
    isSendingReset: boolean;
}

function SecurityTabContent({
    data,
    isLoading,
    user,
    confirmingClear,
    setConfirmingClear,
    confirmingSendReset,
    setConfirmingSendReset,
    onClearLoginAttempts,
    onSendPasswordReset,
    isClearingLogins,
    isSendingReset,
}: SecurityTabContentProps) {
    if (isLoading) {
        return (
            <div className="flex justify-center py-8">
                <Spinner size="lg" />
            </div>
        );
    }

    if (!data) {
        return (
            <p className="text-gray-500 dark:text-gray-400">
                Failed to load data
            </p>
        );
    }

    return (
        <div className="space-y-6">
            {/* Last Login & Password Changed */}
            <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700/50">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        Last Login
                    </p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {formatRelativeTime(data.lastLoginAt)}
                    </p>
                    {data.lastLoginAt && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(data.lastLoginAt).toLocaleString()}
                        </p>
                    )}
                </div>
                <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700/50">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        Password Changed
                    </p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {formatRelativeTime(data.passwordChangedAt)}
                    </p>
                    {data.passwordChangedAt && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(data.passwordChangedAt).toLocaleString()}
                        </p>
                    )}
                </div>
            </div>

            {/* Passkeys */}
            <section>
                <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Passkeys ({data.passkeys.length})
                </h4>
                {data.passkeys.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        No passkeys registered
                    </p>
                ) : (
                    <ul className="rounded-sm border border-gray-200 dark:border-gray-600">
                        {data.passkeys.map((passkey: PasskeySummary) => (
                            <li
                                key={passkey.id}
                                className="flex items-center justify-between border-b border-gray-100 px-3 py-2 last:border-b-0 dark:border-gray-700"
                            >
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    {passkey.deviceName}
                                </span>
                                <span className="text-xs text-gray-500">
                                    Last used: {formatRelativeTime(passkey.lastUsedAt)}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* Active Tokens */}
            <section>
                <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Active Tokens ({data.tokens.length})
                </h4>
                {data.tokens.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        No active tokens
                    </p>
                ) : (
                    <ul className="rounded-sm border border-gray-200 dark:border-gray-600">
                        {data.tokens.map((token: TokenSummary, index: number) => (
                            <li
                                key={index}
                                className="flex items-center justify-between border-b border-gray-100 px-3 py-2 last:border-b-0 dark:border-gray-700"
                            >
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    {formatTokenType(token.type)}
                                </span>
                                <span className={`text-xs ${token.isExpired ? 'text-red-500' : 'text-gray-500'}`}>
                                    {token.isExpired ? 'Expired' : `expires ${formatTimeUntil(token.expiresAt)}`}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* Recent Failed Logins */}
            <section>
                <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Recent Failed Logins ({data.loginAttempts.length})
                </h4>
                {data.loginAttempts.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        No recent failed login attempts
                    </p>
                ) : (
                    <ul className="max-h-40 overflow-y-auto rounded-sm border border-gray-200 dark:border-gray-600">
                        {data.loginAttempts.map((attempt: LoginAttemptSummary, index: number) => (
                            <li
                                key={index}
                                className="flex items-center justify-between border-b border-gray-100 px-3 py-2 last:border-b-0 dark:border-gray-700"
                            >
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    {formatRelativeTime(attempt.attemptedAt)}
                                </span>
                                <span className="text-xs text-gray-500">
                                    IP: {attempt.ipAddress}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* Admin Actions */}
            <section className="border-t border-gray-200 pt-4 dark:border-gray-700">
                <h4 className="mb-3 font-semibold text-gray-900 dark:text-white">
                    Admin Actions
                </h4>
                <div className="flex flex-wrap gap-3">
                    {/* Clear Failed Logins */}
                    {!confirmingClear ? (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setConfirmingClear(true)}
                            disabled={user.failedLoginAttempts === 0}
                        >
                            Clear Failed Logins ({user.failedLoginAttempts})
                        </Button>
                    ) : (
                        <div className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 dark:bg-amber-900/20">
                            <span className="text-sm text-amber-700 dark:text-amber-400">
                                Clear all failed logins?
                            </span>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={onClearLoginAttempts}
                                isLoading={isClearingLogins}
                            >
                                Confirm
                            </Button>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setConfirmingClear(false)}
                                disabled={isClearingLogins}
                            >
                                Cancel
                            </Button>
                        </div>
                    )}

                    {/* Send Password Reset */}
                    {!confirmingSendReset ? (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setConfirmingSendReset(true)}
                        >
                            Send Password Reset
                        </Button>
                    ) : (
                        <div className="flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 dark:bg-blue-900/20">
                            <span className="text-sm text-blue-700 dark:text-blue-400">
                                Send password reset email?
                            </span>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={onSendPasswordReset}
                                isLoading={isSendingReset}
                            >
                                Confirm
                            </Button>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setConfirmingSendReset(false)}
                                disabled={isSendingReset}
                            >
                                Cancel
                            </Button>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
